import React, { useEffect, useState, useRef } from "react";
import { DateRangePicker } from "react-date-range";
import { PrimaryColor } from "../common";
import { enGB } from "date-fns/locale";
import ReactDOM from "react-dom";
import "./styles.css";

export type DateRange = {
  startDate: Date;
  endDate: Date;
  key: string;
};

interface MultiDateRangePickerProps {
  setDates: (ranges: DateRange[]) => void;
  selectedRanges?: DateRange[];
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  minWidth?: number | string;
  maxWidth?: number | string;
  singleDate?: boolean;
}

const formatRangeLabel = (r: DateRange): string => {
  const fmt = (d: Date) => {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}/${mm}/${yy}`;
  };
  const sameDay =
    r.startDate.getFullYear() === r.endDate.getFullYear() &&
    r.startDate.getMonth() === r.endDate.getMonth() &&
    r.startDate.getDate() === r.endDate.getDate();
  return sameDay ? fmt(r.startDate) : `${fmt(r.startDate)} – ${fmt(r.endDate)}`;
};

const isoKey = (r: DateRange): string =>
  `${r.startDate.toISOString().split("T")[0]}_${
    r.endDate.toISOString().split("T")[0]
  }`;

const RANGE_PALETTE = [
  { background: "#FFF0E5", border: "#F3C9A6", text: "#A44E1E" },
  { background: "#EAF4FF", border: "#B8D5F1", text: "#22577A" },
  { background: "#EDF8F0", border: "#B9E1C4", text: "#2B6B45" },
  { background: "#F5ECFF", border: "#D8C2FF", text: "#6942A0" },
  { background: "#FFF8DF", border: "#F2DA97", text: "#8A5A00" },
];

const hashString = (value: string): number => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }

  return hash;
};

const getRangeTheme = (range: DateRange) =>
  RANGE_PALETTE[hashString(isoKey(range)) % RANGE_PALETTE.length];

const buildDisabledDates = (ranges: DateRange[]): Date[] => {
  const disabledDates: Date[] = [];

  ranges.forEach((range) => {
    let current = new Date(range.startDate);
    const end = new Date(range.endDate);

    while (current <= end) {
      disabledDates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
  });

  return disabledDates;
};

export const MultiDateRangePicker: React.FC<MultiDateRangePickerProps> = ({
  setDates,
  selectedRanges = [],
  minDate = "2021-01-01",
  maxDate = "2028-12-31",
  placeholder = "Select Dates",
  minWidth = 200,
  maxWidth = 260,
  singleDate = false,
}) => {
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [ranges, setRanges] = useState<DateRange[]>(selectedRanges);
  const [tempRange, setTempRange] = useState<DateRange>({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });

  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setRanges(selectedRanges);
  }, [selectedRanges]);

  const openPicker = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setTempRange({
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    });
    setMounted(true);
    requestAnimationFrame(() => setIsOpen(true));
  };

  const closePicker = () => {
    setIsOpen(false);
    closeTimeoutRef.current = setTimeout(() => setMounted(false), 250);
  };

  const handleDateChange = (
    item: Record<string, Partial<DateRange> | undefined> | undefined,
  ) => {
    const selection =
      item?.selection ??
      Object.values(item ?? {}).find((range): range is Partial<DateRange> =>
        Boolean(range),
      );

    setTempRange((prev) => {
      const startDate =
        selection?.startDate instanceof Date
          ? selection.startDate
          : prev.startDate;
      const endDate = singleDate
        ? startDate
        : selection?.endDate instanceof Date
          ? selection.endDate
          : startDate;

      return {
        startDate,
        endDate,
        key: "selection",
      };
    });
  };

  const handleAdd = () => {
    const k = isoKey(tempRange);
    if (ranges.some((r) => isoKey(r) === k)) {
      closePicker();
      return;
    }
    const next = [...ranges, { ...tempRange }];
    setRanges(next);
    setDates(next);
    closePicker();
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRanges([]);
    setDates([]);
  };

  const removeRange = (e: React.MouseEvent, idx: number) => {
    e.stopPropagation();
    const next = ranges.filter((_, i) => i !== idx);
    setRanges(next);
    setDates(next);
  };

  const disabledDates = buildDisabledDates(ranges);

  useEffect(() => {
    if (!mounted) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!target.isConnected) return;
      if (buttonRef.current?.contains(target)) return;

      const els = document.querySelectorAll(
        ".date-selection-wrapper, .rdrDefinedRangesWrapper",
      );
      let inside = false;
      els.forEach((el) => {
        if (el.contains(target)) inside = true;
      });
      if (!inside) closePicker();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mounted]);

  const renderPortal = () => {
    if (!mounted) return null;

    const pickerRanges = [
      tempRange,
      ...ranges.map((r) => ({
        startDate: r.startDate,
        endDate: r.endDate,
        key: isoKey(r),
      })),
    ];

    const pickerRangeColors = [
      PrimaryColor,
      ...ranges.map((r) => getRangeTheme(r).background),
    ];

    return ReactDOM.createPortal(
      <>
        <style>{`
          .dp-backdrop { transition: opacity 250ms ease; }
          .dp-backdrop-enter { opacity: 0; }
          .dp-backdrop-open  { opacity: 1; }

          .dp-modal {
            transition: opacity 250ms ease, transform 250ms ease;
          }
          .dp-modal-enter { opacity: 0; transform: scale(0.92) translateY(12px); }
          .dp-modal-open  { opacity: 1; transform: scale(1)    translateY(0);    }
        `}</style>

        <div
          className={`fixed inset-0 z-[9999998] dp-backdrop ${
            isOpen ? "dp-backdrop-open" : "dp-backdrop-enter"
          }`}
          style={{
            background: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(2px)",
          }}
        />

        <div className="fixed inset-0 z-[9999999] flex items-center justify-center pointer-events-none">
          <div
            className={`dp-modal pointer-events-auto ${
              isOpen ? "dp-modal-open" : "dp-modal-enter"
            }`}
          >
            <div
              className="date-selection-wrapper shadow-2xl rounded-xl overflow-hidden"
              style={{
                maxWidth: "96vw",
                maxHeight: "90vh",
                overflow: "auto",
                background: "#fff",
              }}
            >
              <DateRangePicker
                onChange={handleDateChange}
                showPreview={true}
                dragSelectionEnabled={false}
                minDate={new Date(minDate)}
                maxDate={new Date(maxDate)}
                moveRangeOnFirstSelection={false}
                rangeColors={pickerRangeColors}
                color={PrimaryColor}
                locale={enGB}
                months={2}
                ranges={pickerRanges}
                direction="horizontal"
                disabledDates={disabledDates}
                inputRanges={[]}
              />
              <div className="flex justify-center gap-4 py-4 bg-white">
                <button
                  onClick={handleAdd}
                  className="px-6 py-2 bg-[#F1935C] text-white rounded-lg"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      </>,
      document.body,
    );
  };

  const hasSelection = ranges.length > 0;

  return (
    <div className="relative" style={{ minWidth, maxWidth }}>
      <div
        ref={buttonRef}
        role="button"
        tabIndex={0}
        onClick={() => (mounted ? closePicker() : openPicker())}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            mounted ? closePicker() : openPicker();
          }
        }}
        className="mdp-trigger"
      >
        <div className="mdp-chip-area">
          {!hasSelection && (
            <span
              style={{
                color: "#8C8A96",
                fontSize: 12,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {placeholder}
            </span>
          )}

          {hasSelection &&
            ranges.map((range, index) => {
              return (
                <span key={isoKey(range)} className="mdp-pill">
                  <span className="mdp-pill-label">
                    {formatRangeLabel(range)}
                  </span>
                  <button
                    type="button"
                    onClick={(event) => removeRange(event, index)}
                    aria-label={`Remove ${formatRangeLabel(range)}`}
                  >
                    ×
                  </button>
                </span>
              );
            })}
        </div>

        <div className="mdp-clear">
          {hasSelection && (
            <button type="button" onClick={(e) => handleClearAll(e)}>
              ✕ clear
            </button>
          )}
        </div>
      </div>

      {renderPortal()}
    </div>
  );
};

export default MultiDateRangePicker;
